import React, { useEffect, useState } from 'react';
import {
  DEFAULT_CLOSE_TIME,
  DEFAULT_OPEN_TIME,
  openDayHours,
  sellerHoursHasAny,
  sellerHoursSummary,
  SellerHoursMap,
} from '../utils/sellerHours';

type DayKey = keyof SellerHoursMap;

const DAYS: DayKey[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

interface SellerHoursEditorProps {
  hours: SellerHoursMap;
  onChange: (hours: SellerHoursMap) => void;
  error?: string | null;
}

const SellerHoursEditor: React.FC<SellerHoursEditorProps> = ({ hours, onChange, error }) => {
  const [expanded, setExpanded] = useState(() => sellerHoursHasAny(hours) || !!error);
  const summary = sellerHoursSummary(hours);
  const hasError = !!error;

  useEffect(() => {
    if (sellerHoursHasAny(hours) || error) setExpanded(true);
  }, [hours, error]);

  const setDay = (day: DayKey, patch: Partial<SellerHoursMap[DayKey]>) => {
    onChange({
      ...hours,
      [day]: { ...hours[day], ...patch },
    });
  };

  if (!expanded) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className={`w-full rounded-lg border px-4 py-3 text-left transition ${
            hasError
              ? 'border-red-300 bg-red-50/50 hover:bg-red-50'
              : 'border-gray-200 bg-gray-50/80 hover:bg-gray-50'
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-800">Your hours</p>
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                {summary ?? 'When buyers can expect you to fulfill orders'}
              </p>
            </div>
            <span className="text-sm font-medium text-green-600 shrink-0">Set</span>
          </div>
        </button>
        {hasError && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
      </div>
    );
  }

  return (
    <div>
      <div
        className={`rounded-lg border overflow-hidden ${
          hasError ? 'border-red-300' : 'border-gray-200'
        }`}
      >
        <div className="flex items-start justify-between gap-3 px-4 py-3 bg-gray-50 border-b border-gray-100">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-800">Your hours</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Required for individual listings. Applies to all your listings.
            </p>
          </div>
          {!sellerHoursHasAny(hours) && !hasError && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="text-xs text-gray-500 hover:text-gray-700 shrink-0 pt-0.5"
            >
              Hide
            </button>
          )}
        </div>

        <div className="divide-y divide-gray-100">
          {DAYS.map((day) => {
            const row = hours[day];
            const isOpen = !row.closed;
            return (
              <div key={day} className="px-4 py-3">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span className="text-sm font-medium text-gray-800 whitespace-nowrap">{day}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isOpen}
                    aria-label={`${day} ${isOpen ? 'open' : 'closed'}`}
                    onClick={() => {
                      if (isOpen) {
                        setDay(day, { closed: true, open: '', close: '' });
                      } else {
                        setDay(day, {
                          ...openDayHours(),
                          open: row.open || DEFAULT_OPEN_TIME,
                          close: row.close || DEFAULT_CLOSE_TIME,
                        });
                      }
                    }}
                    className={`relative shrink-0 w-9 h-5 rounded-full transition-colors ${
                      isOpen ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                        isOpen ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
                {isOpen ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="time"
                      value={row.open}
                      onChange={(e) => setDay(day, { open: e.target.value })}
                      aria-label={`${day} open`}
                      className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    />
                    <span className="text-gray-300 text-xs shrink-0" aria-hidden>
                      –
                    </span>
                    <input
                      type="time"
                      value={row.close}
                      onChange={(e) => setDay(day, { close: e.target.value })}
                      aria-label={`${day} close`}
                      className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">Closed</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {hasError && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default SellerHoursEditor;
